import * as React from "react";
import { useState, useEffect } from "react";
import { Map, NavigationControl, Popup, useControl } from "react-map-gl";
import { GeoJsonLayer } from "deck.gl";
import { MapboxOverlay as DeckOverlay } from "@deck.gl/mapbox";
import { CircularProgress, Backdrop } from "@mui/material";
import dayjs from "dayjs";
import axios from "axios";
import "mapbox-gl/dist/mapbox-gl.css";
import SelectionService from "../../../services/SelectionService";
import keycloak from "services/keycloak";
import config from "../../../config";
import { useNavigate } from "react-router-dom"; 
import Icon from "@mui/material/Icon";
import {decodeJwt } from "../../../authentication/authModules";

// ✅ import MDAlert
import MDAlert from "components/MDAlert";
import { useTranslation } from "react-i18next";

const MAPBOX_TOKEN = config.MAPBOX_ACCESS_TOKEN;
const API_TOKEN = config.MAP_MATCHING_API_TOKEN;
const API_URL = config.MAP_MATCHING_API_URL;

const INITIAL_VIEW_STATE = {
  latitude: 40.6310772242652,
  longitude: 22.953091034889667,
  zoom: 14,
  bearing: 0,
  pitch: 50,
  controller: true,
};
const MAP_STYLE = "mapbox://styles/mapbox/dark-v11";

function DeckGLOverlay(props) {
  const overlay = useControl(() => new DeckOverlay(props));
  overlay.setProps(props);
  return null;
}

const getGradientColor = (percent) => {
  if(percent === null) return [238, 43, 122]
  // Gradient stops
  const gradientColors = [
    [213, 216, 232], // 0%
    [195, 200, 223], // 10%
    [178, 184, 214], // 20%
    [161, 168, 205], // 30%
    [144, 152, 196], // 40%
    [127, 136, 187], // 50%
    [110, 120, 178], // 60%
    [92, 104, 169],  // 70%
    [75, 88, 160],   // 80%
    [58, 72, 151],   // 90%
    [42, 57, 144]    // 100%
];

  // Ensure the percentage is between 0 and 100
  percent = Math.max(0, Math.min(percent, 100));

  // Calculate the two stops to interpolate between
  const lowerIndex = Math.floor(percent / 10);
  const upperIndex = Math.ceil(percent / 10);

  // Get the colors for the stops
  const lowerColor = gradientColors[lowerIndex];
  const upperColor = gradientColors[upperIndex];

  // Calculate the interpolation factor
  const factor = (percent % 10) / 10;

  // Interpolate between the two colors
  const interpolatedColor = lowerColor.map((lc, i) =>
    Math.round(lc + factor * (upperColor[i] - lc))
  );

  return interpolatedColor;
};
const calculateOffsetPosition = (coordinates, offsetx,offsety) => {
  if (coordinates.length < 2) return coordinates[0];

  // Use the first two points to calculate the direction
  const [lon1, lat1] = coordinates[0];
  const [lon2, lat2] = coordinates[1];

  // Calculate a perpendicular offset vector
  const dx = lon2 - lon1;
  const dy = lat2 - lat1;
  const length = Math.sqrt(dx * dx + dy * dy);
  const offsetX = (-dy / length) * offsetx; // Perpendicular x
  const offsetY = (dx / length) * offsety; // Perpendicular y

  // Return the shifted coordinate
  return [lon1 + offsetX, lat1 + offsetY];
};

function MapViewResult({ incomingPercent, incomingValueType, rowData }) {
  const { t } = useTranslation();

  const [selected, setSelected] = useState(null);
  const [hoverInfo, setHoverInfo] = useState(null);
  const [data, setData] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(INITIAL_VIEW_STATE.zoom);
  const [cursor, setCursor] = useState("auto");
  const [loading, setLoading] = useState(false);
  const [percent, setPercent] = useState(6);
  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);
  const token = localStorage.getItem("keycloak-token");
  let email = "info@toolbox.synchromode.eu";
  let userId = "anonymous"; 
  if (token) {
    try {
      const tokenObject = decodeJwt(token);
      //console.log("Decoded Token:", tokenObject);
      email = tokenObject?.email || "info@toolbox.synchromode.eu"; // Extract userId from token
      userId = tokenObject?.sub || "anonymous"; // Extract userId from token
    } catch (error) {
      console.error("Error decoding JWT:", error);
    }
  }
  // ✅ New: info panel state
  const [infoPanel, setInfoPanel] = useState({ type: null, message: "" });
 const navigate = useNavigate(); // ✅ router navigation

  useEffect(() => {
    const trajectoryMode = SelectionService.getTrajectoryMode() || "combined";
    var wayId = !rowData
      ? SelectionService.getSelectedLink().properties.wayId
      : rowData.primaryWayId;
    var fromDate = !rowData
      ? dayjs(
          SelectionService.getDates()[SelectionService.getSelectedDate()]
            .startDate[0]
        ).format("DD-MM-YYYY")
      : "";
    var toDate = !rowData
      ? dayjs(
          SelectionService.getDates()[SelectionService.getSelectedDate()]
            .endDate[0]
        ).format("DD-MM-YYYY")
      : "";

    var fromTime = "00:00:00";
    var toTime = "00:00:00";
    var user = "anonymous";
    var reportName = !rowData ? SelectionService.getReportName() : "";

    var selectedTime = !rowData
      ? SelectionService.getTimes()[SelectionService.getSelectedTime()]
      : undefined;

    if (selectedTime !== undefined) {
      if (selectedTime.allDay === false) {
        if (selectedTime.fromTime !== null) {
          fromTime = dayjs(selectedTime.from[0]).format("HH:mm:ss");
        }
        if (selectedTime.toTime !== null) {
          toTime = dayjs(selectedTime.to[0]).format("HH:mm:ss");
        }
        if (selectedTime.userId != undefined) {
          user = selectedTime.userId;
          SelectionService.setUserId(user);
        }
      }
    console.log(fromTime);
    console.log(toTime);  
    console.log(selectedTime);
    console.log(SelectionService.getSelectedDate());
    console.log(SelectionService.getReportName());
    console.log(user);
    }

    const fetchData = async () => {
      setLoading(true);
      setInfoPanel({ type: null, message: "" }); // reset alerts

      try {
        var url = `${API_URL}/fusion/trajectories/${wayId}?from=${fromDate} ${fromTime}&to=${toDate} ${toTime}&threshold=${incomingPercent}&name=${reportName}&user=${user}&email=${email}`;

        if (!rowData && trajectoryMode === "fleet") {
          url = `${API_URL}/fleet/trajectories/${wayId}?from=${fromDate} ${fromTime}&to=${toDate} ${toTime}&threshold=${incomingPercent}&name=${reportName}&user=${user}&email=${email}`;
        }

        if (!rowData && trajectoryMode === "car") {
          url = `${API_URL}/GetWayStats2/${wayId}?from=${fromDate} ${fromTime}&to=${toDate} ${toTime}&threshold=${incomingPercent}&name=${reportName}&user=${user}&email=${email}`;
        }

        if (rowData) {
          url = `${API_URL}/report/${rowData.reportId}`;
        }
 console.log(url);
        const response = await axios.get(url, {
          headers: {
            "X-API-Key": API_TOKEN,
          },
          validateStatus: (status) => [200, 202].includes(status),
        });
       
        if (response.status === 200) {
          const filteredData = response.data.features.filter(
            (x) => x.properties.wayId === wayId
          );

          const coordinates = response.data.features.flatMap(
            (feature) => feature.geometry.coordinates
          );

          SelectionService.setResponse(
            !rowData
              ? {
                  features: response.data.features.map(
                    ({ geometry, ...rest }) => rest
                  ),
                  summary: filteredData[0].properties.summary,
                  primaryWayId:
                    SelectionService.getSelectedLink().properties.wayId,
                  userId: userId,
                  name: SelectionService.getReportName(),
                }
              : {}
          );

          // bounding box
          const minLng = Math.min(...coordinates.map((coord) => coord[0]));
          const maxLng = Math.max(...coordinates.map((coord) => coord[0]));
          const minLat = Math.min(...coordinates.map((coord) => coord[1]));
          const maxLat = Math.max(...coordinates.map((coord) => coord[1]));

          setData(response.data);
          setSelected(filteredData);

          setViewState({
            ...viewState,
            longitude: filteredData[0].geometry.coordinates[0][0],
            latitude: filteredData[0].geometry.coordinates[0][1],
            zoom: Math.min(
              16,
              getBoundsZoomLevel(minLng, maxLng, minLat, maxLat)
            ),
            transitionDuration: 1000,
          });
        }

        if (response.status === 202) {
          setInfoPanel({
            type: "info",
            message:
              t("trajectories.wizard.mapViewResult.processing"),
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setInfoPanel({
          type: "error",
          message: error.message || t("trajectories.wizard.mapViewResult.genericError"),
        });
      } finally {
        setLoading(false);
      }
    };

    if (incomingPercent !== percent) {
      setPercent(incomingPercent);
      fetchData();
    }
  }, [incomingPercent, incomingValueType]);

  const handleZoom = (event) => {
    setZoomLevel(event.viewState.zoom);
  };

  const handleHover = (info) => {
    setHoverInfo(info && info.object ? info : null);
  };

  const getBoundsZoomLevel = (minLng, maxLng, minLat, maxLat) => {
    const worldSize = 2000;
    const padding = 0.35;
    const mapWidth = window.innerWidth * (1 - padding);
    const mapHeight = window.innerHeight * (1 - padding);

    const lngDiff = maxLng - minLng;
    const latDiff = maxLat - minLat;

    const zoomLng = Math.log2(worldSize / lngDiff) - 1;
    const zoomLat = Math.log2(worldSize / latDiff) - 1;
    var value = Math.max(zoomLng, zoomLat);
    value = Math.min(value, 17);
    return value;
  };

  var layers = data
    ? [
        new GeoJsonLayer({
          id: "path-layer",
          data: data,
          filled: true,
          stroked: true,
          lineWidthMinPixels: zoomLevel > 15 ? 4 : 2,
          getLineWidth: zoomLevel > 15 ? 8 : 1,
          getPointRadius: zoomLevel > 15 ? 15 : 10,
          getLineColor: (feature) => {
            return getGradientColor(feature.properties.percentage);
          },
          pickable: true,
          autoHighlight: true,
          highlightColor: [0, 0, 0, 80],
          onHover: handleHover,
        }),
        selected
          ? new GeoJsonLayer({
              id: "selected-layer",
              data: {
                type: "FeatureCollection",
                features: selected,
              },
              filled: true,
              stroked: true,
              lineWidthMinPixels: 4,
              getLineWidth: 8,
              getLineColor: [238, 43, 122],
            })
          : null,
      ]
    : [];

  return (
    <>
      <Backdrop open={loading} style={{ zIndex: 999, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>

  {infoPanel.type && (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(0,0,0,0.3)", // optional dim background
      zIndex: 2000,
    }}
  >
    <MDAlert color={infoPanel.type}>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <h4 style={{ margin: 0 }}>{infoPanel.message}</h4>
        <button
          style={{
            alignSelf: "flex-end",
            background: "transparent",
            border: "none",
            color: "#fff",
            fontWeight: "bold",
            cursor: "pointer",
          }}
          onClick={() => {
            setInfoPanel({ type: null, message: "" });
            SelectionService.resetSelection();
            navigate("/trajectories"); // ✅ works now
          }}>
           <Icon fontSize="small">close</Icon>
        </button>
      </div>
    </MDAlert>
  </div>
)}

      <Map
        {...viewState}
        initialViewState={INITIAL_VIEW_STATE}
        mapStyle={MAP_STYLE}
        cursor={cursor}
        mapboxAccessToken={MAPBOX_TOKEN}
        controller={true}
        onZoom={handleZoom}
        onViewStateChange={handleZoom}
        onMove={(evt) => setViewState(evt.viewState)}
      >
        {hoverInfo && zoomLevel > 15 && hoverInfo.object && (
          <Popup
            longitude={hoverInfo.coordinate[0]}
            latitude={hoverInfo.coordinate[1]}
            closeButton={false}
            anchor="bottom"
            style={{ zIndex: 10 }}
          >
            <div>
              <strong>{hoverInfo.object.properties.name}</strong> (
              {hoverInfo.object.properties.wayId})
              <br />
              {incomingValueType === "Percent"
                ? `${t("trajectories.wizard.mapViewResult.popup.percent")}: ${hoverInfo.object.properties.percentage || "N/A"}`
                : ""}
              {incomingValueType !== "Percent"
                ? `${t("trajectories.wizard.mapViewResult.popup.count")}: ${hoverInfo.object.properties.count || "N/A"}`
                : ""}
              <br />
              {incomingValueType !== "Percent"
                ? `${t("trajectories.wizard.mapViewResult.popup.summary")}: ${hoverInfo.object.properties.summary || "N/A"}`
                : ""}
            </div>
          </Popup>
        )}
        <DeckGLOverlay layers={layers} />
        <NavigationControl position="top-left" />
      </Map>
    </>
  );
}

export default MapViewResult;
